import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomTooltip = ({ active, payload, label, userMapByDate }) => {
  if (!active || !payload || !userMapByDate[label]) return null;

  return (
    <div className="bg-white shadow-md border border-gray-200 rounded-lg p-3 max-w-sm">
      <p className="text-sm font-semibold text-gray-800 mb-2">
        📅 {label} — {payload[0]?.value} user(s)
      </p>
      <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
        {userMapByDate[label].map((user) => (
          <div key={user.email} className="flex items-center gap-2">
            <img
              src={user.profileImage || "/person.png"}
              alt={user.firstName}
              className="w-6 h-6 rounded-full object-cover border"
            />
            <div className="text-sm text-gray-700 leading-tight">
              <div className="font-medium">
                {user.firstName} {user.lastName}
                <span className="text-xs text-gray-500 ml-1">
                  @{user.username}
                </span>
              </div>
              <div className="text-[11px] text-gray-400 break-all">
                🆔 {user.id || "N/A"}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


const UserHistory = () => {
  const navigate = useNavigate();
  const [chartData, setChartData] = useState([]);
  const [userMapByDate, setUserMapByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    const fetchUserHistory = async () => {
      const token = localStorage.getItem("token");
      setLoading(true);

      try {
        const res = await fetch(`${BACKEND_URL}/user/admin/all`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Fetch failed");

        const rawUsers = data.users || [];
        const grouped = {};

        rawUsers.forEach((user) => {
          let createdAt = user.createdAt?._seconds
            ? new Date(user.createdAt._seconds * 1000)
            : new Date(user.createdAt);

          if (isNaN(createdAt)) return;

          const key = createdAt.toISOString().split("T")[0];
          if (!grouped[key]) grouped[key] = [];

          // Prevent duplicate users by ID/email in same date group
          const alreadyExists = grouped[key].some(
            (u) => u.id === user.id || u.email === user.email
          );
          if (!alreadyExists) grouped[key].push(user);
        });

        const chartFormatted = Object.entries(grouped)
          .sort(([a], [b]) => new Date(a) - new Date(b))
          .map(([date, users]) => ({
            date,
            users: users.length,
          }));

        setChartData(chartFormatted);
        setUserMapByDate(grouped);
        setLastUpdated(
          new Date().toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        );
      } catch (err) {
        console.error("❌ Error fetching user history:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserHistory();
  }, []);

  return (
    <div className="min-h-screen px-6 py-10 bg-white">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">
            📊 Real-Time User Growth
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Last Updated:{" "}
            <span className="text-blue-600 font-medium">
              {lastUpdated || "Fetching..."}
            </span>
          </p>
        </div>
        <button
          onClick={() => navigate("/fronteracloud/profile/frontera-dashboard")}
          className="text-sm px-4 py-2 rounded bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 transition"
        >
          ← Back to Dashboard
        </button>
      </div>

      {loading ? (
        <div className="text-gray-500 text-sm">Loading live chart...</div>
      ) : (
        <div className="w-full h-[400px] bg-gray-50 border border-gray-200 rounded p-4">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#eee" strokeDasharray="5 5" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip
                content={
                  <CustomTooltip userMapByDate={userMapByDate} />
                }
              />
              <Line
                type="monotone"
                dataKey="users"
                stroke="#4F46E5"
                strokeWidth={3}
                dot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default UserHistory;
